import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from '../_shared';

export interface InstanceSnapshot {
  id: string | number;
  name?: string;
  disk_id?: string | number;
  /** Unix epoch seconds (string). */
  date?: number | string;
  /** Size in MB. */
  size?: string | number;
  parent?: string;
}

export function useInstanceSnapshots(serviceId: string | undefined, instanceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.instanceSnapshots(serviceId ?? '', instanceId ?? ''),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ snapshots?: unknown[] } | unknown[]>(
        await serviceApi.get(onPath(serviceId!, `instances/${instanceId}/snapshots`)),
      );
      const raw = Array.isArray(body) ? body : body.snapshots ?? [];
      // Each row is a SnapshotPool: `{ disk_id, snapshot: <obj|array> }`. Flatten to
      // a flat snapshot list, carrying the pool's disk_id onto each snapshot.
      return raw.flatMap((item) => {
        const pool = item as { disk_id?: string | number; snapshot?: unknown };
        const s = pool.snapshot ?? item;
        const list = (Array.isArray(s) ? s : [s]) as InstanceSnapshot[];
        return list.filter(Boolean).map((snap) => ({ disk_id: pool.disk_id, ...snap }));
      });
    },
  });
}

/** A snapshot as listed across the whole service (`/snapshots`). */
export interface ServiceSnapshot {
  id: string;
  name?: string;
  /** Size in MB. */
  size?: number;
  attached?: { instance_id: string; name: string };
}

/** All instance snapshots across the service (top-level Snapshots page). */
export function useServiceSnapshots(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.snapshots(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ snapshots?: ServiceSnapshot[] } | ServiceSnapshot[]>(
        await serviceApi.get(onPath(serviceId!, 'snapshots')),
      );
      return Array.isArray(body) ? body : body.snapshots ?? [];
    },
  });
}

export function useCreateSnapshot(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, { name: string; disk_id: string | number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; disk_id: string | number }) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/snapshots`), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.instanceSnapshots(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useRevertSnapshot(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, string | number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (snapshotId: string | number) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/snapshots/${snapshotId}/revert`), {})).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteSnapshot(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, string | number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (snapshotId: string | number) =>
      (await serviceApi.delete(onPath(serviceId, `instances/${instanceId}/snapshots/${snapshotId}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.instanceSnapshots(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}
